<template>
  <div :style="`${height !== null ? 'height: ' + (height) + 'px' : ''}`">
    <div class="d-flex justify-content-between">
      <base-text
        size="s"
        nowrap
        light
        class="ml-1"
      >
        {{ title }}
      </base-text>
      <BaseTabs
        v-if="items.length > 1"
        v-model="current"
        class="tabbar-chart-tabs"
        :items="items"
        underlined
      />
    </div>
    <simple-bar-chart
      :key="chartKey"
      class="ml-n3"
      :chart-data="localChartdata"
      :options="chartOptions"
      :styles="height !== null ? { height: `${height*0.9}px`} : {}"
    />
  </div>
</template>

<script>
import styles from '@/styles/_variables.scss';
import SimpleBarChart from './SimpleBarChart';

export default {
  name: 'TabBarChart',
  components: {
    SimpleBarChart,
  },
  props: {
    chartdata: {
      type: Object,
      default() {
        return {
          datasets: {},
        };
      },
    },
    // TODO: Consider including this option inside the `options` prop
    height: {
      type: Number,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    composeLabels: {
      type: Boolean,
      default: false,
    },
    yGrid: {
      type: Boolean,
      default: false,
    },
    labelSize: {
      type: Number,
      default: 13,
    },
    options: {
      type: Object,
      default: () => ({
        /** TODO: Handle what to do with the rest of the defaults when setting just a subset of these options */
        dataLabelsFontSize: 13,
        dataLabelsDisplay: true,
      }),
    },
  },
  data() {
    return {
      chartKey: Date.now(),
      current: 0,
      chartOptions: {
        layout: {
          padding: {
            top: 32,
          },
        },
        plugins: {
          datalabels: {
            display: this.options.dataLabelsDisplay,
            align: 'end',
            anchor: 'end',
            color: '#000000',
            font: {
              family: styles.fontFamilySansSerifLight,
              size: this.options.dataLabelsFontSize,
            },
            formatter(value, ctx) {
              const formatFloat = (number, decimals) => (
                Math.round((number + Number.EPSILON) * 10 ** decimals)
                  / 10 ** decimals
              );
              return `${value < 0 ? '--' : formatFloat(value, 1)}%`;
            },
          },
        },
        tooltips: {
          enabled: false,
        },
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
              ticks: {
                fontFamily: styles.fontFamilySansSerifLight,
                fontSize: this.labelSize,
                fontColor: '#000000',
                minRotation: 0,
                maxRotation: 0,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                display: this.yGrid,
                tickMarkLength: 0,
                zeroLineWidth: 0.5,
                lineWidth: 0.25,
                color: styles.colorBlack,
              },
              display: true,
              ticks: {
                display: this.yGrid,
                padding: 0,
                beginAtZero: true,
                min: 0,
                max: 100,
                fontSize: this.labelSize,
                fontFamily: styles.fontFamilySansSerifLight,
                fontColor: styles.colorGray,
                callback(value, index, values) {
                  return `${value}     `;
                },
              },
            },
          ],
        },
        responsive: true,
        maintainAspectRatio: false,
        hover: { mode: null },
      },
    };
  },
  computed: {
    localChartdata() {
      const chartdata = {};
      chartdata.datasets = [this.chartdata.datasets[this.current]];
      chartdata.labels = this.chartdata.labels;
      return {
        labels: this.chartdata.labels.map((label) => (this.composeLabels
          ? `${label} ${this.items[this.current]}`
          : label)),
        datasets: [
          {
            ...this.chartdata.datasets[this.current],
            label: '',
          },
        ],
      };
    },
    items() {
      return this.chartdata.datasets.map((e) => e.label);
    },
  },
  watch: {
    current(newval) {
      console.log('SELECTED: ', this.items[newval]);
      this.$mixpanel.track('tab_change', {
        location: '/report/summary',
        selected_tab: this.items[newval],
        account_id: this.$store.state.account.user.user.email,
      });
    },
    options: {
      handler(newval) {
        /** TODO: Currently just updating data labels display. Update other options if needed (font sizes) */
        this.chartKey = Date.now();
        this.chartOptions.plugins.datalabels.display = newval.dataLabelsDisplay;
      },
      deep: true,
    },
    yGrid: {
      handler(newVal) {
        this.chartOptions = {
          ...this.chartOptions,
          scales: {
            ...this.chartOptions.scales,
            yAxes: [{
              ...this.chartOptions.scales.yAxes[0],
              gridlines: {
                ...this.chartOptions.scales.yAxes[0].gridLines,
                display: newVal,
              },
              ticks: {
                ...this.chartOptions.scales.yAxes[0].ticks,
                display: newVal,
              },
            }],
          },
        };
      },
      immediate: true,
    },
    labelSize: {
      handler(newVal) {
        this.chartOptions = {
          ...this.chartOptions,
          scales: {
            ...this.chartOptions.scales,
            xAxes: [{
              ...this.chartOptions.scales.xAxes[0],
              ticks: {
                ...this.chartOptions.scales.xAxes[0].ticks,
                fontSize: newVal,
              },
            }],
            yAxes: [{
              ...this.chartOptions.scales.yAxes[0],
              ticks: {
                ...this.chartOptions.scales.yAxes[0].ticks,
                fontSize: newVal,
              },
            }],
          },
        };
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.tabbar-chart-tabs {
  &::v-deep .base-tabs__item {
    font-size: 0.75rem !important;
    padding-top: 0;
    margin: 0 0 .5rem 0;
    min-height: inherit;
    &.active {
      font-weight: 600;
    }
  }
}
::v-deep .base-text.ml-1 {
  letter-spacing: -0.2px;
}

</style>
